import { UserIdentityFlowAction, type IdentityOrchestratorFlowDescriptor, type UserIdentityStatusDto } from "@/services/UserIdentitiesService";
import { resolveEffectiveFlowAction } from "@/screen/Identities/IdentityForm/identityFlowNormalizer";

/** Lo mínimo que se necesita del envelope de saveUserIdentity para decidir si hay un flujo que ofrecer. */
export interface SaveConflictResult
{
    success: boolean;
    flow: IdentityOrchestratorFlowDescriptor | undefined;
}

/**
 * Acciones que el guardado puede devolver (HTTP 409) cuando el DNI escrito ya existe en otra identidad y que
 * tienen salida en el formulario: panel propio o, en AcceptTransferOwnership, la edición de esa identidad.
 */
export const SaveConflictFlowActions: readonly UserIdentityFlowAction[] =
[
    UserIdentityFlowAction.RequestAccess,
    UserIdentityFlowAction.RequestOwnershipTransfer,
    UserIdentityFlowAction.AutomaticOwnershipTransfer,
    UserIdentityFlowAction.ReactivateOwn,
    UserIdentityFlowAction.AcceptTransferOwnership,
];

/**
 * Convierte un guardado fallido por DNI ya existente en el estado de flujo que pinta el panel de esa acción,
 * igual que si hubiera salido del análisis del documento. Devuelve null cuando no hay salida que ofrecer y se
 * debe mostrar el error tal cual.
 *
 *  - `dni`: el escrito en el formulario (trim + mayúsculas). La cesión automática lo envía como prueba.
 *  - `existingDraftId`: la identidad existente, que es la que recibe la solicitud. Sin ella las acciones de
 *    acceso y cesión no se ofrecen: el controlador caería en el id del borrador que se está editando y la
 *    solicitud iría contra el propio interviniente. Recuperar uno propio solo necesita el DNI. Aceptar una
 *    cesión también la necesita: es la identidad cuya edición se abre.
 */
export function buildIdentityStatusFromSaveResult(result: SaveConflictResult | null | undefined, typedDni: string | null | undefined): UserIdentityStatusDto | null
{
    if(!result || result.success)
    {
        return null;
    }

    const action = resolveEffectiveFlowAction(result.flow ?? undefined);
    if(action === null || !SaveConflictFlowActions.includes(action))
    {
        return null;
    }

    const dni = String(typedDni ?? "").trim().toUpperCase();
    if(dni === "")
    {
        return null;
    }

    const rawExistingId = Number(result.flow?.existingIdentityId ?? 0);
    const existingIdentityId = Number.isInteger(rawExistingId) && rawExistingId > 0 ? rawExistingId : null;
    if(existingIdentityId === null && action !== UserIdentityFlowAction.ReactivateOwn)
    {
        return null;
    }

    return{
        dni: dni,
        action: action,
        existingDraftId: existingIdentityId,
        existingPersonName: null,
        requiresOmniaSync: false,
        isIncomplete: false,
    };
}
